import type { OrdersApiResponse, OrdersApiRow, GroupedOrdersBasket } from '../../types';
import rawOrders from './ordersRaw.json';

// Basket/strategy name lookup (not in DB export — mapped manually)
const basketNames: Record<string, string> = {
  '72b571a1-64f0-48f1-af46-7b1b6071e270': 'Nifty Options',
  '8bcd5678-0000-48f1-af46-000000000wat': 'WAT Testing',
  '36e8e14d-2a40-439c-9270-7b5efa3c13c2': 'Nifty Weekly',
};

const strategyNames: Record<string, string> = {
  'b988c205-e603-499d-9b91-a6cf528f665f': 'ATM CE Buy',
  '9961c764-b569-41d2-aab0-c517ae1b7537': 'CE Scalper',
  'b10a1405-69b0-4250-a628-50d14d0971ee': 'CE Momentum',
  '24ff90c6-08dc-4325-ab40-e08723ebda90': 'CE SL Hunter',
  '0fd36606-04cd-4e7e-b91c-3c4632b569e3': 'Deep ITM CE',
  'c93b517d-a918-4cb6-b4ef-3dcd90fd046f': 'ITM CE Swing',
  '3aab1234-0000-4e25-a01c-000000000wat': 'WAT SL Strategy',
  'c26ce6ca-671c-44a5-a937-3cc03e4c996d': 'OTM Lottery',
};

const STATUS_MAP: Record<string, OrdersApiRow['status']> = {
  COMPLETE: 'SUCCESS',
  OPEN: 'OPEN',
  REJECTED: 'FAILED',
  CANCELLED: 'CANCELLED',
  'TRIGGER_PENDING': 'PARTIAL',
  EXITED: 'SUCCESS',
};


function transformOrder(r: any): OrdersApiRow {
  const tag = r.order_tag;
  return {
    basketId: r.basket_id || '',
    basketName: basketNames[r.basket_id] || '',
    strategyId: r.strategy_id || '',
    strategyName: strategyNames[r.strategy_id] || '',
    symbol: r.symbol || '',
    exchange: r.exchange || '',
    clientId: r.client_id || '',
    broker: r.broker || '',
    positionId: r.order_tag || '',
    side: r.action || '',
    tag: tag || '',
    qty: Number(r.quantity) || 0,
    price: Number(r.average_price) || 0,
    product: r.product || '',
    time: r.created_at || '',
    status: STATUS_MAP[r.status] || 'FAILED',
  };
}

const allOrders: OrdersApiRow[] = rawOrders.map(transformOrder);

// Add hardcoded rejected orders for UI testing
const rejectedOrders: OrdersApiRow[] = [
  {
    basketId: '72b571a1-64f0-48f1-af46-7b1b6071e270',
    basketName: 'Nifty Options',
    strategyId: 'b988c205-e603-499d-9b91-a6cf528f665f',
    strategyName: 'ATM CE Buy',
    symbol: 'NIFTY26APR24500CE',
    exchange: 'NFO',
    clientId: 'RD0397',
    broker: 'ZEBU',
    positionId: 'mock_rejected_ord_01',
    side: 'BUY',
    tag: 'QE_mock_rejected_ord_01',
    qty: 75,
    price: 0,
    product: 'NRML',
    time: '2026-04-05T09:20:00+05:30',
    status: 'FAILED',
    rejectionReason: 'RMS:Rule: Check freeze qty per order limit in NFO',
  },
  {
    basketId: '36e8e14d-2a40-439c-9270-7b5efa3c13c2',
    basketName: 'Nifty Weekly',
    strategyId: 'c26ce6ca-671c-44a5-a937-3cc03e4c996d',
    strategyName: 'OTM Lottery',
    symbol: 'BANKNIFTY26APR54000PE',
    exchange: 'NFO',
    clientId: 'K255245',
    broker: 'ZEBU',
    positionId: 'mock_rejected_ord_02',
    side: 'SELL',
    tag: 'QE_mock_rejected_ord_02',
    qty: 30,
    price: 0,
    product: 'MIS',
    time: '2026-04-05T09:25:00+05:30',
    status: 'FAILED',
    rejectionReason: 'Insufficient margin: required 45000, available 12000',
  },
];

allOrders.push(...rejectedOrders);

function buildGrouped(orders: OrdersApiRow[]): GroupedOrdersBasket[] {
  const basketMap = new Map<string, { basketId: string; basketName: string; strategies: Map<string, { strategyId: string; strategyName: string; orders: OrdersApiRow[] }> }>();

  for (const o of orders) {
    if (!basketMap.has(o.basketId)) {
      basketMap.set(o.basketId, { basketId: o.basketId, basketName: o.basketName, strategies: new Map() });
    }
    const basket = basketMap.get(o.basketId)!;
    if (!basket.strategies.has(o.strategyId)) {
      basket.strategies.set(o.strategyId, { strategyId: o.strategyId, strategyName: o.strategyName, orders: [] });
    }
    basket.strategies.get(o.strategyId)!.orders.push(o);
  }

  return Array.from(basketMap.values()).map(b => ({
    basketId: b.basketId,
    basketName: b.basketName,
    strategies: Array.from(b.strategies.values()),
  }));
}

function buildSummary(orders: OrdersApiRow[]): OrdersApiResponse['summary'] {
  return {
    total: orders.length,
    complete: orders.filter(o => o.status === 'SUCCESS').length,
    rejected: orders.filter(o => o.status === 'FAILED').length,
    cancelled: orders.filter(o => o.status === 'CANCELLED').length,
    open: orders.filter(o => o.status === 'OPEN' || o.status === 'PARTIAL').length,
  };
}

export const mockOrdersResponse: OrdersApiResponse = {
  orders: allOrders,
  baskets: buildGrouped(allOrders),
  summary: buildSummary(allOrders),
};
